import { useRef } from 'react';
import styled from 'styled-components';
import { Skeleton, Theme, Tooltip, Typography } from '@mui/material';
import Humanize from 'humanize-plus';
import { ReactComponent as InfoIcon } from '../../../assets/info-icon.svg';
import { Flex, FlexColumn } from '../../../components/Flex';
import useHover from '../../../hooks/useHover';
import { fontSize, fontWeight, space } from '../../../style/utils';

const VALUE_FONT_SIZE = '37px';

const Wrapper = styled(FlexColumn)`
  min-width: 225px;
  width: 225px;
  gap: ${space('smMinus')};
  align-items: center;
`;

const Value = styled(Typography)`
  font-size: ${VALUE_FONT_SIZE};
  font-weight: ${fontWeight('light')};
  :hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const Details = styled(Flex)`
  align-items: center;
  gap: ${space('md')};
`;

const Divider = styled.div`
  height: 18px;
  width: 1px;
  background: ${({ theme }) => theme.palette.surface.emphasis};
`;

type DetailStatus = 'success' | 'warning' | 'error';

const Detail = styled(Typography)<{ color: DetailStatus }>`
  font-size: ${fontSize('xxxl')};
  color: ${({ theme, color }: { theme: Theme; color: DetailStatus }) =>
    theme.palette[color].main};
  :hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const Title = styled(Typography)`
  font-size: ${fontSize('xl')};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: ${({ theme }) => theme.palette.content.secondary};
`;

const TitleWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
`;

type Props = {
  title: string;
  data: {
    title: string;
    passed: number;
    warnings: number;
    failures: number;
    errors: number;
  };
  tooltip: string;
  onValueClick: () => void;
  onLabelClick: (statuses: string[]) => void;
  loading?: boolean;
};

const SummaryBox = ({
  title,
  data,
  tooltip,
  onValueClick,
  onLabelClick,
  loading,
}: Props) => {
  const wrapperRef = useRef<any>();
  const isHover = useHover(wrapperRef);

  return (
    <Wrapper ref={wrapperRef} aria-label={`${title} summary`}>
      {loading ? (
        <FlexColumn sx={{ gap: 1, alignItems: 'center' }}>
          <FlexColumn>
            <Skeleton variant="text" width={50} height={32} />
            <Skeleton variant="text" width={50} height={57} />
          </FlexColumn>
          <Flex sx={{ gap: 1 }}>
            <Skeleton variant="text" width={30} height={34} />
            <Skeleton variant="text" width={30} height={34} />
            <Skeleton variant="text" width={30} height={34} />
          </Flex>
        </FlexColumn>
      ) : (
        <>
          <TitleWrapper>
            <Flex sx={{ gap: 0.7, alignItems: 'center', ml: '19px' }}>
              <Title>{title}</Title>
              <Tooltip
                title={tooltip}
                sx={{ visibility: isHover ? 'visible' : 'hidden' }}
              >
                <Flex>
                  <InfoIcon />
                </Flex>
              </Tooltip>
            </Flex>
          </TitleWrapper>
          <Value onClick={onValueClick} aria-label={`${title} total value`}>
            {Humanize.formatNumber(data.passed + data.warnings + data.failures)}
          </Value>
          <Details divider={<Divider />}>
            <Detail
              color="success"
              onClick={() => onLabelClick(['passed'])}
              aria-label={`${title} passed count`}
            >
              {Humanize.formatNumber(data.passed)}
            </Detail>
            <Detail
              color="warning"
              onClick={() => onLabelClick(['warning'])}
              aria-label={`${title} warnings count`}
            >
              {Humanize.formatNumber(data.warnings)}
            </Detail>
            <Detail
              color="error"
              onClick={() => onLabelClick(['failed', 'error'])}
              aria-label={`${title} failures count`}
            >
              {Humanize.formatNumber(data.failures + data.errors)}
            </Detail>
          </Details>
        </>
      )}
    </Wrapper>
  );
};

export default SummaryBox;
